import i18next from 'i18next';

import {sourceConstants} from "../_constants";
import {__isAvailable, __isEmpty, __isKeyAvailable} from "./commons/utility.helper";

export const sourceHelper = {
    parseSources,
    formatSourceTimeDuration,
    prepareParamsForApi,
    updateSourceList,
    prepareSourceAnchorLinks,
    isSourceInList,
};

/**
 * This method takes in the source request responses and prepares for component consumption
 * @param rawSources This is the response from the services backend
 */
function parseSources(rawSources){

    const sourceFormatter = (src) => {
        return {
            id: src.uuid,
            name: src.title,
            uploaded: formatSourceDate(src.createdDate),
            viewed: src.viewCount,
            status: parseSourceStatus(src.currentStatus),
            url: window.location.origin.toString() + "/play/" + src.uuid,
            embedCode: getEmbedCode(src),
            imageUrl: src.thumbnailUri,
            stickerUrl: getSticker(src),
            duration: src.duration
        };
    };

    if(Array.isArray(rawSources)) {
        const sortDescending = (a,b) => a.createdDate < b.createdDate ? 1 : -1;
        return rawSources.sort(sortDescending).map((src) => sourceFormatter(src));
    } else {
        return __isAvailable(rawSources.uuid)
            ? sourceFormatter(rawSources)
            : null;
    }
}

/**
 * Returns the sticker url for the given source
 *
 * @param src the source to probe
 * @returns {*} thumbnail-mini (if available), else fallback to thumbnail
 */
const getSticker = (src) => {
    return (__isKeyAvailable(src, "thumbnailMiniUri") && src.thumbnailMiniUri !== "")
                ? src.thumbnailMiniUri // mini-thumbnail
                : src.thumbnailUri;    // fallback to thumbnail
}

/**
 * Returns the embed code for the given source
 *
 * @param src the source for which embed code is needed
 * @returns {string} the iframe embed code
 */
const getEmbedCode = (src) => {
    return '<iframe ' +
                'src="' + window.location.origin.toString() + '/embed/' + src.uuid + '" ' +
                'frameborder="0" ' +
                'allowfullscreen="true" ' +
                'height="395" ' +
                'width="700" ' +
                'style="min-height: 350px;">' +
            '</iframe>'
}

/**
 * This method assigns a status based on status assigned at backend
 * @param status The status assigned at server
 * @returns {string} The status to be used for end-users
 */
const parseSourceStatus = (status) =>{
    let srcStatus;
    if(status === sourceConstants.SOURCE_STATUS_PUBLISHED){
        srcStatus = sourceConstants.SOURCE_STATE.ACTIVE;
    } else if (status === sourceConstants.SOURCE_STATUS_READY) {
        srcStatus = sourceConstants.SOURCE_STATE.DEACTIVATED;
    } else {
        srcStatus = sourceConstants.SOURCE_STATE.PENDING;
    }
    return srcStatus;
};

/**
 * This method calculates 'ago' formatted time based on creationDate
 * @param date date createdDate
 * @returns {string} x 'unit' ago
 */
const formatSourceDate = (date) =>{
    const t = i18next.t;

    let readableDate;
    if(date === null) {
        readableDate = t('a long time ago');
    } else {
        try {
            // server date is in UTC - calculate as per information from client
            const moment = require('moment-timezone');
            const serverCreatedDate = moment.tz(date, "UTC");
            const clientZone = moment.tz.guess(Boolean);
            const clientCreatedDate = serverCreatedDate.clone().tz(clientZone).toISOString();

            // find the difference
            let diff = moment.duration(moment().diff(clientCreatedDate)).asMinutes();

            if(diff < 0) {  // the created time is in future - throw error
                // noinspection ExceptionCaughtLocallyJS
                throw "Unexpected date " + clientCreatedDate;
            }

            const humanizeDuration = require('humanize-duration');          // used for custom cutoffs
            if (diff <= 90) {                   // if less than 90 minutes : display in minutes
                diff = (diff < 1)? 1 : diff;    // minimum unit should be 1 minute ago
                readableDate = humanizeDuration(diff * 60 * 1000,{ units: ['m'] , round: true });
            } else if (diff < 24 * 60) {        // if less than a day : display in hours
                readableDate = humanizeDuration(diff * 60 * 1000,{ units: ['h'] , round: true });
            } else {                            // for everything else
                readableDate = moment.duration(diff * -1, 'm').humanize();  // used for generic return
            }
        } catch (e) {
            readableDate = t("some time");
        }
    }
    return readableDate;
};

/**
 * Format the time-based duration into human-readable format
 *
 * @param duration in seconds
 * @returns {string} humanized format for time
 */
function formatSourceTimeDuration(duration){
    let readableDuration;

    const humanizeDuration = require('humanize-duration');

    // internationalization might be added with the library (https://www.npmjs.com/package/humanize-duration)
    if( duration < 120 ){                               // less than 120 seconds
        readableDuration = humanizeDuration( duration * 1000, { units: ['s'] , round: true })
    } else if ( duration <= (120 * 60) ){               // 2 minutes to 2 hours
        /*
         * Services does a ceiling to the next minutes, while the client rounds-off.
         * Fixing the discrepancy (in minute format only) of having two different values for minutes.
         * Rounding off the duration (seconds) to the nearest minute.
         */
        const durationAfterCeil = ( Math.ceil(duration/60) ) * 60;
        readableDuration = humanizeDuration( durationAfterCeil * 1000, { units: ['m'] , round: true })
    } else if ( duration > (120 * 60) ){                // more than 2 hours
        readableDuration = humanizeDuration( duration * 1000, { units: ['h'] , round: true })
    } else {
        readableDuration = "";
    }
    return readableDuration;
}

/**
 * This function will prepare the parameter and its values for sending to server
 * @param param the parameter to be updated - this parallels the class field name on the services
 * @param value the new value to be replaced with
 */
function prepareParamsForApi(param, value){
    let data = {};
    data[param] = value;
    return data;
}

/**
 * This function will accept old list and an updated source and return an updated list
 * @param list the old list
 * @param updatedSource the source from the old list that was recently updated
 * @returns {*} updated list that contains the updated source
 */
function updateSourceList(list, updatedSource){
    if(__isEmpty(updatedSource)) {
        return list;
    }

    return list.map( l => {
        // if uuid match then return the updated one
        if(l.id === updatedSource.id){
            return updatedSource;
        }
        return l;
    });
}

/**
 * This function checks if the given source is in the list or not
 *
 * @param list the list of sources
 * @param source the source to check
 * @returns {*} indicates whether the source is in the list or not
 */
function isSourceInList(list, source) {
    if(__isEmpty(list) || __isEmpty(source)) {
        return false;
    }
    return list.some( l => (l.id === source.id));
}

/**
 * This function accepts an array of uuid and build play urls anchor links for them
 *
 * @param uuids array of uuids
 * @returns {*} array of play urls for the given uuids with anchor formatting
 */
function prepareSourceAnchorLinks(uuids) {
    let sourceLinks = [];
    uuids.forEach( (uuid) => {
        const playUuid = "/play/" + uuid;
        sourceLinks.push(`<br /><a href=${playUuid}>See Gist</a>`);
    });
    return sourceLinks;
}